import clsx from 'clsx';
import { useState } from 'react';
import { TenantData } from '../../backend/queries/tenants';
import { postJsonAsync } from '../../utils/fetch';

export type ArticleFeedbackProps = {
    tenant: TenantData;
    articleId: bigint;
    articleContentId: bigint;
};

enum FeedbackState {
    NoInteraction,
    WaitingForExtended,
    Done
}

enum FeedbackType {
    Positive,
    Neutral,
    Negative
}

type FeedbackButtonProps = {
    isSelected: boolean;
    isDisabled?: boolean;
    onClick: () => void;
    emoji: string;
};

function FeedbackButton({ isSelected, isDisabled, onClick, emoji }: FeedbackButtonProps) {
    return <button className={clsx("border rounded p-1 text-xl border-gray-200 bg-white  hover:border-gray-300 hover:bg-gray-50 font-[sans-serif]", isSelected && "border-primary-bg-soft-border bg-primary-bg-soft-bg")}
        type='button' onClick={() => onClick()} disabled={isDisabled}>
        {emoji}
    </button>;
}

function getAnonymousUserId() {
    let id = localStorage.getItem("articleFeedback.anonymousUserId");
    if (!id) {
        id = "anonymous_" + Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
        localStorage.setItem("articleFeedback.anonymousUserId", id);
    }
    return id;
}

export function ArticleFeedback({ articleId, articleContentId, tenant: { texts, feedback, projectId, projectCode } }: ArticleFeedbackProps) {

    const [state, setState] = useState<FeedbackState>(FeedbackState.NoInteraction);
    const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
    const [extendedFeedback, setExtendedFeedback] = useState("");

    function handleFeedback(type: FeedbackType) {
        if (type === FeedbackType.Positive) {
            setState(FeedbackState.Done);
        }
        else {
            setState(FeedbackState.WaitingForExtended);
        }
        setFeedbackType(type);

        try {
            postJsonAsync('/api/internal/articles/feedback', {
                articleId: articleId.toString(),
                articleContentId: articleContentId.toString(),
                projectId: projectId.toString(),
                projectCode,
                rating: type === FeedbackType.Positive ? 1 : type === FeedbackType.Negative ? -1 : 0,
                anonymousUserId: getAnonymousUserId(),
            }).catch(e => console.error(e));

        } catch (e) {
            console.error(e);
        }
    }

    function handleExtendedFeedback() {

        setState(FeedbackState.Done);

        const finalExtendedFeedback = extendedFeedback.trim();

        if (finalExtendedFeedback.length > 0) {
            try {
                postJsonAsync('/api/internal/articles/feedback', {
                    articleId: articleId.toString(),
                    articleContentId: articleContentId.toString(),
                    projectId: projectId.toString(),
                    projectCode,
                    text: finalExtendedFeedback,
                    anonymousUserId: getAnonymousUserId(),
                }).catch(e => console.error(e));

            } catch (e) {
                console.error(e);
            }
        }
    }

    return (
        <div className="flex flex-col items-center py-10 my-10 gap-3 border-t border-gray-200">
            <h2 className="text-xl font-bold text-center text-gray-700">{texts.articleFeedbackTitle}</h2>
            <div className="flex gap-2">
                <FeedbackButton emoji={feedback.positiveEmoji} isSelected={feedbackType === FeedbackType.Positive} onClick={() => handleFeedback(FeedbackType.Positive)} />
                <FeedbackButton emoji={feedback.neutralEmoji} isSelected={feedbackType === FeedbackType.Neutral} onClick={() => handleFeedback(FeedbackType.Neutral)} />
                <FeedbackButton emoji={feedback.negativeEmoji} isSelected={feedbackType === FeedbackType.Negative} onClick={() => handleFeedback(FeedbackType.Negative)} />
            </div>
            {state === FeedbackState.WaitingForExtended &&
                <div className='flex flex-col items-center gap-2'>
                    <p className='text-xs text-gray-600 font-medium'>{texts.articleFeedbackAdditionalQuestion}</p>
                    <textarea className="w-80 p-2 border rounded border-gray-200 text-sm placeholder:text-gray-400 bg-white" placeholder={texts.articleFeedbackAdditionalQuestionPlaceholder}
                        value={extendedFeedback} onChange={e => setExtendedFeedback(e.target.value)}></textarea>
                    <button type='button' className='border border-gray-200 p-2 rounded font-bold text-sm bg-white hover:border-gray-300  hover:bg-gray-50'
                        onClick={handleExtendedFeedback}>{texts.articleFeedbackAdditionalQuestionSubmitButton}</button>
                </div>}
            {state === FeedbackState.Done && <div className='text-xs font-medium text-gray-600'>{texts.articleFeedbackThankYouMessage}</div>}
        </div>
    );
}